import React, { useEffect, useRef } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { useSpring, animated } from "react-spring";
import styles from "./InteractiveMap.module.scss";
import useInteractiveMap from "./useInteractiveMap";
import InteractiveMapHandler from "./InteractiveMapHandler";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function InteractiveMap({
  highlightedCountries = [],
  onCountryClick,
  onCountryHover,
  center = [0, 20],
  zoom = 1,
  onMoveEnd,
  onGeographiesLoad,
}) {
  const { stateInteractiveMap, setInteractiveMap } = useInteractiveMap();
  const geographiesLoadedRef = useRef(false);

  const handlers = InteractiveMapHandler(
    stateInteractiveMap,
    setInteractiveMap,
    onCountryClick,
    onCountryHover,
    onMoveEnd
  );

  // Animate center and zoom changes
  const animatedProps = useSpring({
    centerX: center[0],
    centerY: center[1],
    zoom: zoom,
    config: { mass: 1, tension: 180, friction: 26 },
  });

  return (
    <div className={styles.Container}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 140,
        }}
        className={styles.Map}
      >
        <ZoomableGroup 
          center={[animatedProps.centerX.get(), animatedProps.centerY.get()]} 
          zoom={animatedProps.zoom.get()}
          onMoveEnd={handlers.handleMoveEnd}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) => {
              // Load geographies once
              if (!geographiesLoadedRef.current && geographies.length > 0 && onGeographiesLoad) {
                geographiesLoadedRef.current = true;
                onGeographiesLoad(geographies);
              }

              return geographies.map((geo) => {
                const isHighlighted = highlightedCountries.includes(
                  geo.id
                );
                const isHovered =
                  stateInteractiveMap.hoveredCountry === geo.id;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => handlers.handleMouseEnter(geo)}
                    onMouseLeave={handlers.handleMouseLeave}
                    onClick={() => handlers.handleClick(geo)}
                    style={{
                      default: {
                        fill: handlers.getFillColor(isHighlighted, isHovered),
                        stroke: "#FFFFFF",
                        strokeWidth: 0.5,
                        outline: "none",
                      },
                      hover: {
                        fill: handlers.getHoverColor(isHighlighted),
                        stroke: "#FFFFFF",
                        strokeWidth: 0.75,
                        outline: "none",
                      },
                      pressed: {
                        fill: "#C84978",
                        stroke: "#FFFFFF",
                        strokeWidth: 0.75,
                        outline: "none",
                      },
                    }}
                    className={
                      isHighlighted ? styles.HighlightedCountry : styles.Country
                    }
                  />
                );
              });
            }}
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
}
